<template>
    <div :class="['upload', {'has-files': uploads.length}]">
        <FileUpload
            ref="upload"
            v-model="uploads"
            data-cy="upload"
            :accept="acceptableFiles.join()"
            :drop="acceptDrop"
            :maximum="maximumUploads"
            :multiple="multiple"
            :size="acceptableSize"
            @input-filter="filter"
            @input="fileInputs"
        >
            <div
                v-if="!uploads.length"
                class="drag-text"
            >
                <div
                    for="upload"
                    class="d-flex align-items-center font-weight-bold"
                    data-cy="text-box"
                >
                    <FontAwesomeIcon
                        icon="cloud-upload-alt"
                        class="cloud mr-1"
                    />
                    <div
                        v-if="acceptDrop"
                    >
                        <span
                            class="text-secondary"
                        >
                            Drag and drop {{ multiple ? 'files' : 'file' }} here or click to
                        </span>
                        <a
                            href="#"
                        >
                            choose {{ multiple ? 'files' : 'file' }}
                        </a>
                        <small class="d-block text-secondary">{{ acceptedFileTypes }}</small>
                    </div>
                    <div
                        v-else
                    >
                        <span
                            class="text-secondary"
                        >
                            click to
                        </span>
                        <a
                            href="#"
                        >
                            choose {{ multiple ? 'files' : 'file' }}
                        </a>
                        <small class="d-block text-secondary">{{ acceptedFileTypes }}</small>
                    </div>
                </div>
            </div>
        </FileUpload>

        <div
            v-if="uploads.length"
            class="d-flex flex-wrap w-100"
        >
            <div
                v-for="upload in uploads"
                :key="upload.id"
                class="image-tile d-flex flex-column align-items-center justify-content-start"
                :class="customClass"
            >
                <div
                    class="delete"
                >
                    <FloatingActionButton
                        :id="`fabDeletePopConfirm${upload.id}`"
                        data-cy="fabDelete"
                        class="ml-2"
                        type="delete"
                    />

                    <PopConfirm
                        placement="top"
                        :target="`fabDeletePopConfirm${upload.id}`"
                        title="Are you sure you want to delete this file?"
                        @confirmed="remove(upload)"
                    />
                </div>

                <img
                    v-if="isImage(upload.type)"
                    ref="image"
                    :src="upload.thumb"
                    :alt="upload.name"
                    class="thumbnail img-fluid my-2"
                >

                <FontAwesomeIcon
                    v-else
                    :icon="['fal', icon(upload.type)]"
                    class="thumbnail img-fluid my-2"
                />

                <div class="font-weight-bold image-name">
                    {{ upload.name }}
                </div>

                <slot :upload="upload" />

                <div
                    v-if="upload.error === 'size'"
                    class="error"
                >
                    <FontAwesomeIcon icon="times-circle" />
                    File exceeds maximum upload storage capacity
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import {library} from '@fortawesome/fontawesome-svg-core';
    import {
        faFile,
        faFileAlt,
        faFileAudio,
        faFileCode,
        faFileCsv,
        faFileExcel,
        faFilePdf,
    } from '@fortawesome/pro-light-svg-icons';
    import {faCloudUploadAlt} from '@fortawesome/pro-solid-svg-icons';
    import {FontAwesomeIcon} from '@fortawesome/vue-fontawesome';
    import FileUpload from 'vue-upload-component';

    import FloatingActionButton from '../components/FloatingActionButton.vue';
    import PopConfirm from '../components/PopConfirm.vue';

    library.add(
        faCloudUploadAlt,
        faFile,
        faFileAlt,
        faFileAudio,
        faFileCode,
        faFileCsv,
        faFileExcel,
        faFilePdf,
    );

    export default {
        name: 'Upload',
        components: {
            FileUpload,
            FloatingActionButton,
            FontAwesomeIcon,
            PopConfirm
        },
        props: {
            /**
            * When set to `true`, this component can accept more than one file upload at a time.
            * Help text will change from singular or plural based on value.
            */
            multiple: {
                type: Boolean,
                required: false,
                default: false,
            },
            /**
            * Sets acceptable file types, if no types are passed this prop defaults to accept all
            * file types currently allowed by the platform.
            */
            acceptableFiles: {
                type: Array,
                required: false,
                default: () => [
                    'image/svg+xml',
                    'image/jpeg',
                    'image/png',
                    'application/msword',
                    'application/vnd.ms-excel',
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    'application/pdf',
                    'audio/mpeg',
                    'text/csv',
                    'text/plain'
                ]
            },
            /**
            * Sets component to accept dropped files, otherwise, the user will have to click to upload files.
            */
            acceptDrop: {
                type: Boolean,
                required: false,
                default: true,
            },
            /**
            * Sets the maximum byte size for a file.
            */
            acceptableSize: {
                type: Number,
                required: false,
                default: 1024 * 1024
            },
            /**
             * Sets custom classes on the Upload component.
             */
            customClass: {
                type: String,
                // `null`
                default: null,
            },
            /**
             * Sets maximum number of uploads for the Upload component.
             */
            maximumUploads: {
                type: Number,
                // `null`
                default: null,
            },
        },
        data() {
            return {
                uploads: [],
            };
        },
        computed: {
            acceptedFileTypes() {
                let acceptableFileMap = {
                        'image/svg+xml': 'svg',
                        'image/jpeg': 'jpeg',
                        'image/png': 'png',
                        'application/msword': 'word',
                        'application/vnd.ms-excel': 'excel',
                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'excel',
                        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'word',
                        'application/pdf': 'pdf',
                        'audio/mpeg': 'mpeg',
                        'text/csv': 'csv',
                        'text/plain': 'text',
                    },
                    acceptableFileNames = Object.entries(acceptableFileMap)
                        .flatMap(file => this.acceptableFiles.includes(file[0]) ? [file[1]] : []);

                return acceptableFileNames.length > 1
                    ? [...new Set(acceptableFileNames)].slice(0, -1).join(', ') + ' or ' + acceptableFileNames.slice(-1)
                    : acceptableFileNames.toString();
            }
        },
        methods: {
            validFileTypes(file) {
                return this.acceptableFiles.includes(file.type);
            },
            icon(mimeType) {
                return {
                    'application/msword': 'file-alt',
                    'application/vnd.ms-excel': 'file-excel',
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'file-excel',
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'file-alt',
                    'application/pdf': 'file-pdf',
                    'audio/mpeg': 'file-audio',
                    'text/csv': 'file-csv',
                    'text/plain': 'file-code',
                }[mimeType] || 'file';
            },
            isImage(mimeType) {
                return mimeType.includes('image');
            },
            filter(newFile, oldFile, prevent) {
                // Filters unaccepted file types
                if (newFile && !this.validFileTypes(newFile)) {
                    return prevent();
                }

                // Adds a thumbnail to the file object, if the file is an image
                if (newFile && (!oldFile || newFile.file !== oldFile.file) && newFile.type.includes('image') !== -1) {
                    let URL = window.URL || window.webkitURL;
                    if (URL && URL.createObjectURL) {
                        newFile.thumb = URL.createObjectURL(newFile.file);
                    }
                }
            },
            remove(file, $event = null) {
                this.$refs.upload.remove(file);
                $event?.target?.blur();

                this.$emit('remove', file);
            },
            fileInputs($event) {
                this.$emit('input', $event);
            }
        }
    };
</script>
